'use client';
import React, { useState } from 'react';
import Badge from '@/components/ui/Badge';
import { AlertTriangle } from 'lucide-react';
import ComplaintDetailModal from './ComplaintDetailModal';

type ComplaintStatus = 'open' | 'under_review' | 'resolved' | 'reprocess';

interface Complaint {
  id: string;
  jobId: string;
  customer: string;
  cleaner: string;
  service: string;
  date: string;
  issueType: string;
  description: string;
  status: ComplaintStatus;
  photos: number;
  photoUrls: string[];
  createdAt: string;
  priority: 'high' | 'medium' | 'low';
}

const mockComplaints: Complaint[] = [
  {
    id: 'comp-001',
    jobId: 'job-004',
    customer: 'Rachel Kim',
    cleaner: 'James Okafor',
    service: 'Deep Clean',
    date: 'May 15, 2026',
    issueType: 'Missed area or surface',
    description: 'The oven interior was not cleaned despite being listed in the deep clean service. There is still visible grease on the oven walls.',
    status: 'open',
    photos: 2,
    photoUrls: [
      'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=300&fit=crop',
      'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=400&h=300&fit=crop',
    ],
    createdAt: '2 hours ago',
    priority: 'high',
  },
  {
    id: 'comp-002',
    jobId: 'job-007',
    customer: 'Tom Nguyen',
    cleaner: 'Marcus Johnson',
    service: 'Standard Clean',
    date: 'May 14, 2026',
    issueType: 'Arrived significantly late',
    description: 'Cleaner arrived 1.5 hours after the scheduled 10:00 AM window without any communication.',
    status: 'under_review',
    photos: 0,
    photoUrls: [],
    createdAt: '1 day ago',
    priority: 'medium',
  },
  {
    id: 'comp-003',
    jobId: 'job-009',
    customer: 'Priya Sharma',
    cleaner: 'Devon Richards',
    service: 'Move-Out Clean',
    date: 'May 10, 2026',
    issueType: 'Damaged item',
    description: 'A framed photo was knocked over and the glass frame was cracked during the cleaning.',
    status: 'open',
    photos: 3,
    photoUrls: [
      'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop',
      'https://images.unsplash.com/photo-1513694203232-719a280e022f?w=400&h=300&fit=crop',
      'https://images.unsplash.com/photo-1484154218962-a197022b5858?w=400&h=300&fit=crop',
    ],
    createdAt: '1 week ago',
    priority: 'high',
  },
  {
    id: 'comp-004',
    jobId: 'job-002',
    customer: 'Marcus Webb',
    cleaner: 'Maria Gonzalez',
    service: 'Standard Clean',
    date: 'May 6, 2026',
    issueType: 'Missed area or surface',
    description: 'Baseboards in the master bedroom were not cleaned.',
    status: 'resolved',
    photos: 1,
    photoUrls: [
      'https://images.unsplash.com/photo-1505691938895-1758d7feb511?w=400&h=300&fit=crop',
    ],
    createdAt: '11 days ago',
    priority: 'low',
  },
];

const statusVariant = (s: ComplaintStatus) => {
  if (s === 'open') return 'danger';
  if (s === 'under_review') return 'warning';
  if (s === 'reprocess') return 'info';
  return 'success';
};

const statusLabel = (s: ComplaintStatus) => {
  if (s === 'open') return 'Open';
  if (s === 'under_review') return 'Under Review';
  if (s === 'reprocess') return 'Reprocess';
  return 'Resolved';
};

const priorityColor: Record<string, string> = {
  high: 'text-danger',
  medium: 'text-warning',
  low: 'text-muted-foreground',
};

export default function AdminComplaintsTable() {
  const [complaints, setComplaints] = useState(mockComplaints);
  const [viewComplaint, setViewComplaint] = useState<Complaint | null>(null);

  const updateStatus = (id: string, status: ComplaintStatus) => {
    setComplaints(prev => prev.map(c => c.id === id ? { ...c, status } : c));
    setViewComplaint(prev => prev?.id === id ? { ...prev, status } : prev);
  };

  const handleReprocessCreated = (complaintId: string) => {
    setComplaints(prev => prev.map(c => c.id === complaintId ? { ...c, status: 'reprocess' } : c));
  };

  return (
    <>
      <div className="bg-card rounded-2xl border border-border card-shadow overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <p className="text-sm font-bold text-foreground">Complaints & Issues</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {complaints.filter(c => c.status !== 'resolved').length} open issues requiring attention
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1 text-xs text-danger font-semibold">
              <AlertTriangle size={13} />
              {complaints.filter(c => c.priority === 'high' && c.status !== 'resolved').length} high priority
            </span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                {['Customer', 'Cleaner', 'Service', 'Issue Type', 'Priority', 'Status', 'Photos', 'Reported'].map(h => (
                  <th key={`ch-${h}`} className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {complaints.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => setViewComplaint(c)}
                  className="hover:bg-muted/30 transition-colors cursor-pointer"
                  title="Click to view full details"
                >
                  <td className="px-4 py-3">
                    <p className="text-sm font-semibold text-foreground">{c.customer}</p>
                    <p className="text-xs text-muted-foreground">{c.jobId}</p>
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">{c.cleaner}</td>
                  <td className="px-4 py-3 text-sm text-foreground whitespace-nowrap">{c.service}</td>
                  <td className="px-4 py-3 text-sm text-foreground max-w-[160px] truncate">{c.issueType}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-bold uppercase ${priorityColor[c.priority]}`}>{c.priority}</span>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={statusVariant(c.status)} label={statusLabel(c.status)} size="sm" />
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground font-tabular">{c.photos}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{c.createdAt}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ComplaintDetailModal
        complaint={viewComplaint}
        onClose={() => setViewComplaint(null)}
        onStatusUpdate={updateStatus}
        onReprocessCreated={handleReprocessCreated}
      />
    </>
  );
}